import { useState, useEffect } from "react";
import { RotateCcw, Volume2, Paintbrush } from "lucide-react";
import TaskProgress, { type TaskStatus } from "./TaskProgress";

const SuccessFeedback = ({
  onNext,
  onPracticeOther,
  recordedLanguage,
  title = "Great Job",
  message = "Great Job. You nailed it!!!",
  taskStatus,
}: any) => {
  const [showContent, setShowContent] = useState(false);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const timer1 = setTimeout(() => setShowContent(true), 300);
    const timer2 = setTimeout(() => setShowButton(true), 800);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const otherLanguage = recordedLanguage === "telugu" ? "English" : "Telugu";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 p-4">
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-md w-full text-center border border-white/20">
        {taskStatus && <TaskProgress taskStatus={taskStatus} />}

        <div className="relative mb-6">
          <div
            className={`transform transition-all duration-1000 ${
              showContent ? "scale-100 rotate-0" : "scale-0 rotate-180"
            }`}
          >
            <div className="relative inline-block">
              <img
                src="/success.gif"
                alt="Happy Owl"
                className="w-32 h-32 rounded-full shadow-lg"
              />
              <div
                className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
              <div
                className="absolute -top-4 left-2 w-3 h-3 bg-pink-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.4s" }}
              ></div>
              <div
                className="absolute -bottom-2 -left-2 w-3 h-3 bg-blue-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.6s" }}
              ></div>
            </div>
          </div>
        </div>

        <div
          className={`transform transition-all duration-700 delay-300 ${
            showContent ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            {title}
          </h1>
        </div>

        <div
          className={`transform transition-all duration-700 delay-500 ${
            showContent ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <p className="text-lg text-green-600 font-medium mb-8">{message}</p>
        </div>

        <div
          className={`transform transition-all duration-700 delay-700 ${
            showButton
              ? "translate-y-0 opacity-100 scale-100"
              : "translate-y-4 opacity-0 scale-95"
          }`}
        >
          <div className="flex flex-col gap-3 items-center">
            <button
              onClick={onNext}
              className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-3 mx-auto"
            >
              <Paintbrush
                size={24}
                className="group-hover:rotate-12 transition-transform duration-300"
              />
              Practice Drawing
            </button>

            <button
              onClick={onPracticeOther}
              className="group bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-3 mx-auto"
            >
              <Volume2
                size={24}
                className="group-hover:scale-110 transition-transform duration-300"
              />
              Practice {otherLanguage}
            </button>
          </div>
        </div>

        <div className="absolute top-4 right-4 w-20 h-20 opacity-20">
          <div
            className="w-full h-full border-4 border-purple-300 rounded-full animate-spin"
            style={{ animationDuration: "3s" }}
          ></div>
        </div>
      </div>
    </div>
  );
};

const ErrorFeedback = ({
  onTryAgain,
  onDraw,
  onSkip,
  onPracticeOther,
  recordedLanguage,
  title = "Try Again",
  message = "Hmmm not quite right. Please try again",
  taskStatus,
}: any) => {
  const [showContent, setShowContent] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  useEffect(() => {
    const timer1 = setTimeout(() => setShowContent(true), 300);
    const timer2 = setTimeout(() => setShowButtons(true), 800);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const otherLanguage = recordedLanguage === "telugu" ? "English" : "Telugu";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 p-4">
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-md w-full text-center border border-white/20">
        {taskStatus && <TaskProgress taskStatus={taskStatus} />}

        <div className="relative mb-6">
          <div
            className={`transform transition-all duration-1000 ${
              showContent ? "scale-100 rotate-0" : "scale-0 -rotate-180"
            }`}
          >
            <div className="relative inline-block">
              <div className="w-40 h-40 rounded-full flex items-center justify-center shadow-lg">
                <img
                  src="/try-again.gif"
                  alt="Happy Owl"
                  className="w-32 h-32 rounded-full shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          className={`transform transition-all duration-700 delay-300 ${
            showContent ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-red-500 to-pink-500 bg-clip-text text-transparent">
            {title}
          </h1>
        </div>

        <div
          className={`transform transition-all duration-700 delay-500 ${
            showContent ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <p className="text-lg text-orange-600 font-medium mb-8">{message}</p>
        </div>

        <div
          className={`transform transition-all duration-700 delay-700 ${
            showButtons
              ? "translate-y-0 opacity-100 scale-100"
              : "translate-y-4 opacity-0 scale-95"
          }`}
        >
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={onTryAgain}
              className="group bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white px-8 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <RotateCcw
                size={20}
                className="group-hover:-rotate-45 transition-transform duration-300"
              />
              Try Again
            </button>

            <button
              onClick={onSkip}
              className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Volume2
                size={20}
                className="group-hover:scale-110 transition-transform duration-300"
              />
              Skip Word
            </button>

            {/* Row 3: Practice other language */}
            <button
              onClick={onDraw}
              className="group bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Paintbrush
                size={20}
                className="group-hover:rotate-12 transition-transform duration-300"
              />
              Draw
            </button>

            <button
              onClick={onPracticeOther}
              className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Volume2
                size={20}
                className="group-hover:scale-110 transition-transform duration-300"
              />
              Practice {otherLanguage}
            </button>
          </div>
        </div>

        <div className="absolute top-6 left-6 w-16 h-16 opacity-20">
          <div className="w-full h-full border-3 border-red-300 rounded-lg animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

type AudioFeedBackProps = {
  isSuccess?: boolean;
  onSkip: () => void;
  tryAgain?: () => void;
  goToDrawingPage?: () => void;
  onPracticeOther?: () => void;
  recordedLanguage?: "english" | "telugu";
  taskStatus?: TaskStatus;
};

const AudioFeedback = (props: AudioFeedBackProps) => {
  const [currentScreen, setCurrentScreen] = useState("success");

  const {
    isSuccess,
    onSkip,
    tryAgain,
    goToDrawingPage,
    onPracticeOther,
    recordedLanguage,
    taskStatus,
  } = props;

  useEffect(() => {
    if (isSuccess) {
      setCurrentScreen("success");
    } else {
      setCurrentScreen("error");
    }
  }, [isSuccess]);

  return (
    <div>
      {currentScreen === "success" && (
        <SuccessFeedback
          onNext={goToDrawingPage}
          onPracticeOther={onPracticeOther}
          recordedLanguage={recordedLanguage}
          taskStatus={taskStatus}
        />
      )}

      {currentScreen === "error" && (
        <ErrorFeedback
          onTryAgain={tryAgain}
          onDraw={goToDrawingPage}
          onSkip={onSkip}
          onPracticeOther={onPracticeOther}
          recordedLanguage={recordedLanguage}
          taskStatus={taskStatus}
        />
      )}
    </div>
  );
};

export default AudioFeedback;
